import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import CommandPage from './CommandPage';
import DeviceGrid from '../components/DeviceGrid';
import DeviceService from '../services/DeviceService';
import ApiService from '../services/ApiService';
import VirtualDeviceService from '../services/VirtualDeviceService';
import VirtualDevicePanel from '../components/VirtualDevicePanel';
import VersionInfo from '../components/VersionInfo';
import DebugPage from './DebugPage';
import DebugPanel from '../components/DebugPanel';
import { Device } from '../components/DeviceCard';

const Dashboard: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [activeTab, setActiveTab] = useState<'command' | 'devices' | 'debug'>('command');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showVirtualDevices, setShowVirtualDevices] = useState<boolean>(false);
  const [apiResponses, setApiResponses] = useState<any[]>([]);
  const [showDebugPanel, setShowDebugPanel] = useState<boolean>(true);
  
  // Scroll to top when navigating to dashboard
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Function to load devices
  const loadDevices = async () => {
    setIsLoading(true);
    try {
      // Make a direct API call without authentication headers
      // This will work with the anonymous access pattern in the backend
      const response = await fetch(`${process.env.REACT_APP_HTTP_API_URL || 'https://k5tlfv2y58.execute-api.us-east-1.amazonaws.com/prod'}/devices`);
      
      const data = await response.json();
      
      if (data && Array.isArray(data.devices)) {
        // Map API device format to frontend device format
        const mappedDevices = data.devices.map((apiDevice: any) => {
          // Use deviceId if id is not available or vice versa
          const id = apiDevice.id || apiDevice.deviceId;
          
          // Check if the device is virtual based on id or isVirtual flag
          const isVirtual = (id && id.toString().startsWith('virtual-')) || apiDevice.isVirtual === true;
          
          // Use either room or location for the room field
          const room = apiDevice.room || apiDevice.location || 'Unknown';
          
          // Ensure state is an object
          const state = apiDevice.state || {};
          
          // Convert any string numeric values to numbers for the frontend
          Object.keys(state).forEach(key => {
            if (typeof state[key] === 'string' && !isNaN(Number(state[key]))) {
              state[key] = Number(state[key]);
            }
          });
          
          return {
            id: id,
            name: apiDevice.name || 'Unnamed Device',
            type: apiDevice.deviceType || 'switch',
            room: room,
            isVirtual: isVirtual,
            state: state
          };
        });
        
        setDevices(mappedDevices);
        console.log('Loaded devices directly from API:', mappedDevices);
      } else {
        console.error('Invalid device data format:', data);
        // Fallback to DeviceService
        const deviceData = await DeviceService.getDevices();
        setDevices(deviceData);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
      // Fallback to DeviceService
      const deviceData = await DeviceService.getDevices();
      setDevices(deviceData);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load devices on component mount
  useEffect(() => {
    // Check URL parameters for tab selection
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'command' || tabParam === 'devices' || tabParam === 'debug') {
      setActiveTab(tabParam as 'command' | 'devices' | 'debug');
    }
    
    // Check for virtual devices parameter
    const virtualParam = urlParams.get('virtual');
    if (virtualParam === 'true') {
      setShowVirtualDevices(true);
    }
    
    // Load devices and set up refresh interval
    loadDevices();
    
    // Refresh devices every 10 seconds to ensure we have the latest data
    const refreshInterval = setInterval(() => {
      if (activeTab === 'devices') {
        console.log('Auto-refreshing devices');
        loadDevices();
      }
    }, 10000);
    
    // Set up real-time device updates
    const handleDeviceUpdate = (message: any) => {
      if (message.event === 'deviceUpdate' && message.deviceId && message.state) {
        setDevices(prevDevices => 
          prevDevices.map(device => 
            device.id === message.deviceId 
              ? { ...device, state: { ...device.state, ...message.state } }
              : device
          )
        );
      }
    };
    
    // Add message handler
    ApiService.addMessageHandler(handleDeviceUpdate);
    
    // Clean up on unmount
    return () => {
      ApiService.removeMessageHandler(handleDeviceUpdate);
      DeviceService.cleanup();
      clearInterval(refreshInterval);
    };
  }, [activeTab]);
  
  // Handle device updates
  const handleUpdateDevice = async (deviceId: string, updates: Partial<Device['state']>) => {
    try {
      console.log(`Updating device ${deviceId} with:`, updates);
      
      // Update the device through the service
      await DeviceService.updateDevice(deviceId, updates);
      
      // The real-time update will come through WebSocket, but update UI immediately for responsiveness
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.id === deviceId 
            ? { ...device, state: { ...device.state, ...updates } }
            : device
        )
      );
      
      // Refresh devices after a short delay to ensure we have the latest state
      setTimeout(() => {
        DeviceService.getDevices().then(deviceData => {
          setDevices(deviceData);
        }).catch(error => {
          console.error('Error refreshing devices:', error);
        });
      }, 1000);
    } catch (error) {
      console.error('Error updating device:', error);
    }
  };
  
  // Process API response
  const processApiResponse = async (response: any) => {
    console.log('API Response:', response);
    
    // Add response to the apiResponses array for debugging
    setApiResponses(prev => [...prev, { timestamp: new Date().toISOString(), response }]);
    
    // Check if this is a device control intent
    if (response.intent === "ControlDevice" && response.type === "device_control") {
      const deviceName = response.device;
      const action = response.action;
      
      if (deviceName && action) {
        try {
          console.log(`Processing device command: ${action} for ${deviceName}`);
          
          // Use the DeviceService to process the command
          const result = await DeviceService.processCommand(deviceName, action);
          console.log('Command result:', result);
          
          // Force immediate refresh of devices after command
          setIsLoading(true);
          await loadDevices();
          setIsLoading(false);
          
          // Schedule another refresh after a delay to catch any delayed state changes
          setTimeout(async () => {
            try {
              await loadDevices();
            } catch (refreshError) {
              console.error('Error in delayed refresh:', refreshError);
            }
          }, 2000);
        } catch (error) {
          console.error('Error processing device command:', error);
        }
      }
    }
  };
  
  // Update URL when tab changes and reload devices if needed
  const handleTabChange = (tab: 'command' | 'devices' | 'debug') => {
    setActiveTab(tab);
    
    // Update URL without reloading the page
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    
    // Remove hash when switching tabs
    if (url.hash && tab !== 'command') {
      url.hash = '';
    }
    
    window.history.pushState({}, '', url);
    
    // Reload devices when switching to devices tab
    if (tab === 'devices') {
      setIsLoading(true);
      // Use our direct API call method to ensure we get fresh data
      loadDevices();
    }
  };
  
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Ivyron Smart Home</h1>
        <nav className="dashboard-nav">
          <button 
            className={`nav-tab ${activeTab === 'command' ? 'active' : ''}`}
            onClick={() => handleTabChange('command')}
          >
            Command
          </button>
          <button 
            className={`nav-tab ${activeTab === 'devices' ? 'active' : ''}`}
            onClick={() => handleTabChange('devices')}
          >
            Devices
          </button>
          <button 
            className={`nav-tab ${activeTab === 'debug' ? 'active' : ''}`}
            onClick={() => handleTabChange('debug')}
          >
            Debug
          </button>
          {activeTab === 'devices' && (
            <button 
              className={`virtual-toggle ${showVirtualDevices ? 'active' : ''}`}
              onClick={() => {
                const newValue = !showVirtualDevices;
                setShowVirtualDevices(newValue);
                
                // Update URL without reloading the page
                const url = new URL(window.location.href);
                if (newValue) {
                  url.searchParams.set('virtual', 'true');
                } else {
                  url.searchParams.delete('virtual');
                }
                window.history.pushState({}, '', url);
              }}
            >
              {showVirtualDevices ? 'Hide Virtual Devices' : 'Show Virtual Devices'}
            </button>
          )}
        </nav>
      </header>
      
      <main className="dashboard-content">
        {activeTab === 'command' ? (
          <CommandPage onLexResponse={processApiResponse} />
        ) : activeTab === 'devices' ? (
          <>
            {showVirtualDevices && (
              <VirtualDevicePanel 
                onDevicesCreated={(newDevices) => {
                  setDevices(prevDevices => [...prevDevices, ...newDevices]);
                  // Reload all devices after a short delay to ensure we have the latest data
                  setTimeout(() => loadDevices(), 1000);
                }} 
              />
            )}
            <DeviceGrid 
              devices={devices} 
              onUpdateDevice={handleUpdateDevice}
              isLoading={isLoading}
            />
          </>
        ) : (
          <DebugPage />
        )}
      </main>
      
      {/* Debug Panel */}
      <DebugPanel 
        devices={devices}
        apiResponses={apiResponses}
        isExpanded={showDebugPanel}
      />
      
      {/* Version is now displayed in App.tsx footer */}
    </div>
  );
};

export default Dashboard;